import React from 'react';
import { observer } from 'mobx-react';
import { ASP_CAMPAIGNS, ASP_YEAR, AspKategorien, aspKategorieModifier, aspPrepStart, campaignsByKategorie, formatAspDateShort, IAspCampaign } from './asp.mock.data';

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const yearStart = new Date(ASP_YEAR, 0, 1).getTime();
const yearEnd = new Date(ASP_YEAR + 1, 0, 1).getTime();

const pct = (d: Date) => {
	const p = ((d.getTime() - yearStart) / (yearEnd - yearStart)) * 100;
	return Math.max(0, Math.min(100, p));
};

// heutiges Datum simuliert innerhalb des Kampagnenjahres
const TODAY = new Date(ASP_YEAR, 6, 6);

// Monatslinien + Heute-Linie, wird in jeder Zeile wiederholt, sodass eine durchgängige Linie entsteht
const TimelineGrid = observer((props: { children?: React.ReactNode; isHeader?: boolean }) => {
	return (
		<div className={`asp-timeline-grid ${props.isHeader ? 'is-header' : ''}`}>
			{MONTH_LABELS.map((_m, i) => (
				<div key={i} className="asp-month-line" style={{ left: `${(i / 12) * 100}%` }} />
			))}
			<div className="asp-today-line" style={{ left: `${pct(TODAY)}%` }} />
			{props.children}
		</div>
	);
});

const CampaignBar = observer((props: { campaign: IAspCampaign }) => {
	const c = props.campaign;
	const modifier = aspKategorieModifier[c.kategorie];
	const prep = aspPrepStart(c);
	const left = pct(c.start);
	const width = pct(c.end) - left;
	const prepLeft = pct(prep);
	const prepWidth = left - prepLeft;

	return (
		<>
			<div
				className={`asp-timeline-bar is-prep ${modifier}`}
				data-tooltip={`Vorbereitung ${formatAspDateShort(prep)} – ${formatAspDateShort(c.start)}`}
				style={{ left: `${prepLeft}%`, width: `${prepWidth}%` }}
			/>
			<div
				className={`asp-timeline-bar ${modifier}`}
				data-tooltip={`${c.name}: ${formatAspDateShort(c.start)} – ${formatAspDateShort(c.end)}`}
				style={{ left: `${left}%`, width: `${width}%` }}
			/>
		</>
	);
});

export const AspTimeline = observer(() => {
	return (
		<div className="pad-1rem">
			<div className="page-header content">
				<h2>Aktivitätsschwerpunkte {ASP_YEAR}</h2>
			</div>

			<div className="pad-bottom-1rem">
				<small className="color-grey">
					{ASP_CAMPAIGNS.length} Kampagnen im Jahr {ASP_YEAR}. Vorbereitungs-/Trainingsphase beginnt ca. 2 Wochen vor Kampagnenstart.
				</small>
			</div>

			<div className="asp-timeline">
				{/* Monats-Kopfzeile */}
				<div className="asp-timeline-row">
					<div className="asp-timeline-label">
						<strong>Kampagne</strong>
					</div>
					<TimelineGrid isHeader={true}>
						{MONTH_LABELS.map((m, i) => (
							<small key={m} className="asp-month-label color-grey" style={{ left: `${(i / 12) * 100}%` }}>
								{m}
							</small>
						))}
						<small className="asp-today-label" style={{ left: `${pct(TODAY)}%` }}>
							Heute
						</small>
					</TimelineGrid>
				</div>

				{AspKategorien.map((kategorie) => (
					<React.Fragment key={kategorie}>
						{/* Kategorie-Zeile */}
						<div className="asp-timeline-row">
							<div className={`asp-timeline-label ${aspKategorieModifier[kategorie]}`}>
								<strong>{kategorie}</strong>
							</div>
							<TimelineGrid isHeader={true} />
						</div>
						{campaignsByKategorie(kategorie).map((c) => (
							<div className="asp-timeline-row" key={c.id}>
								<div className="asp-timeline-label is-campaign">
									{c.name}
									<br />
									<small className="color-grey">
										{formatAspDateShort(c.start)} – {formatAspDateShort(c.end)}
									</small>
								</div>
								<TimelineGrid>
									<CampaignBar campaign={c} />
								</TimelineGrid>
							</div>
						))}
					</React.Fragment>
				))}
			</div>

			{/* Legende */}
			<div className="asp-legend is-centered">
				{AspKategorien.map((k) => (
					<span key={k} className="asp-inline">
						<span className={`asp-kategorie-dot ${aspKategorieModifier[k]}`} />
						<span>{k}</span>
					</span>
				))}
				<span className="asp-inline">
					<span className="asp-kategorie-dot is-prep" />
					<span>Vorbereitungsphase</span>
				</span>
				<span className="asp-inline">
					<span className="asp-kategorie-dot is-today" />
					<span>Heute</span>
				</span>
			</div>
		</div>
	);
});
